import { useEffect, useState } from 'react';

interface UseFakeProgressGeneratorProps {
  /**
   * Duration in seconds to reach 99% progress
   */
  duration: number;
  /**
   * When true, progress will reach 100%
   */
  completed?: boolean;
  /**
   * Optional callback when progress updates
   */
  onProgressChange?: (progress: number) => void;
}

interface UseFakeProgressGeneratorReturn {
  /**
   * Raw progress value (0-100) with decimal precision
   */
  rawValue: number;
  /**
   * Formatted progress value rounded to nearest integer
   */
  formattedValue: number;
}

/**
 * Hook that generates fake progress from 0% to 99% over a specified duration,
 * with slower progression towards the end. Only reaches 100% when completed is true.
 *
 * @param duration - Time in seconds to reach 99%
 * @param completed - When true, progress will reach 100%
 * @param onProgressChange - Optional callback when progress updates
 * @returns Object with rawValue and formattedValue
 */
export function useFakeProgressGenerator({
  duration,
  completed = false,
  onProgressChange,
}: UseFakeProgressGeneratorProps): UseFakeProgressGeneratorReturn {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (completed) {
      setProgress(100);
      onProgressChange?.(100);
      return;
    }

    // Reset progress when duration changes
    setProgress(0);
    onProgressChange?.(0);

    // Calculate update interval for smooth animation (update every 100ms)
    const updateInterval = 100;
    const totalUpdates = (duration * 1000) / updateInterval;

    let currentUpdate = 0;
    const interval = setInterval(() => {
      currentUpdate++;

      // Use exponential approach to slow down near the end
      // Formula ensures we reach exactly 99% at the end
      const normalizedTime = currentUpdate / totalUpdates;

      if (normalizedTime >= 1) {
        // Ensure we reach exactly 99% at the end
        setProgress(99);
        onProgressChange?.(99);
        clearInterval(interval);
        return;
      }

      // Use a formula that guarantees 99% at t=1
      // progress = 99 * (1 - (1-t)^3) gives a nice curve that slows down at the end
      const newProgress = 99 * (1 - Math.pow(1 - normalizedTime, 3));

      setProgress(newProgress);
      onProgressChange?.(newProgress);
    }, updateInterval);

    return () => clearInterval(interval);
  }, [duration, completed, onProgressChange]);

  return {
    rawValue: progress,
    formattedValue: Math.round(progress),
  };
}
